const API_BASE = 'https://api.sms.registermysite.com/api';

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    const isAdminApi = path.startsWith('/admin');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Only force navigation away for normal app APIs, not admin diagnostics
    if (!path.startsWith('/auth') && !isAdminApi) {
      window.location.href = '/login';
    }
    const data = await res.json().catch(() => ({}));
    throw new Error(
      (data as { error?: string }).error ||
      (isAdminApi
      ? 'Unauthorized (admin) — re-login or check is_admin / JWT'
      : 'Unauthorized')
    );
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      (data as { error?: string }).error || `Request failed (${res.status})`
    );
  }
  return data as T;
}

export const api = {
  register: (email: string, password: string, name?: string) =>
  request<{ user: any; token: string }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name }),
  }),
  login: (email: string, password: string) =>
  request<{ user: any; token: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request<{ user: any }>('/auth/me'),

  lookup: (phone: string) =>
  request<any>('/lookup', { method: 'POST', body: JSON.stringify({ phone }) }),
  bulkLookup: (text: string) =>
  request<any>('/lookup/bulk', { method: 'POST', body: JSON.stringify({ text }) }),

  sendSms: (payload: {
    phone?: string;
    gatewayEmail?: string;
    subject?: string;
    body: string;
    fromName?: string;
    lookupId?: string;
  }) => request<any>('/sms/send', { method: 'POST', body: JSON.stringify(payload) }),
  previewGateway: (phone: string, carrier?: string) =>
  request<any>('/sms/preview', {
    method: 'POST',
    body: JSON.stringify({ phone, carrier }),
  }),

  lookups: (params?: { limit?: number; offset?: number; carrier?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    if (params?.carrier) q.set('carrier', params.carrier);
    return request<{ results: any[] }>(`/history/lookups?${q}`);
  },
  messages: (params?: { limit?: number; offset?: number; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    if (params?.status) q.set('status', params.status);
    return request<{ results: any[] }>(`/history/messages?${q}`);
  },
  stats: () => request<any>('/history/stats'),
  exportLookups: () => `${API_BASE}/history/export/lookups`,
  exportMessages: () => `${API_BASE}/history/export/messages`,

  gateways: () => request<{ gateways: any[] }>('/gateways'),
  settings: () => request<any>('/settings'),
  updateSettings: (data: any) =>
  request('/settings', { method: 'PUT', body: JSON.stringify(data) }),

  adminUsers: () => request<{ users: any[] }>('/admin/users'),
  banUser: (id: string, reason?: string) =>
  request(`/admin/users/${id}/ban`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),
  unbanUser: (id: string) =>
  request(`/admin/users/${id}/unban`, { method: 'POST' }),
  deleteUser: (id: string) =>
  request(`/admin/users/${id}`, { method: 'DELETE' }),
};
